import type { CustomerReviewItem } from "../../../services/customerReviews";

const STORAGE_PREFIX = "agiliza:customer-review:last-submitted";

export type CustomerReviewPromptStorageState = {
  lastSubmittedDateKey: string;
  lastSubmittedAt: string | null;
  // Atendimentos pendentes que o cliente ja tinha visto ao enviar ou fechar o
  // convite. Comparar ids nao depende do relogio do servidor nem do aparelho.
  seenAppointmentIds?: string[];
};

export function getLocalDateKey(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function normalizeStoragePart(value?: string | null): string {
  return String(value || "")
    .trim()
    .toLowerCase();
}

export function buildCustomerReviewPromptStorageKey(
  slug?: string | null,
  customerId?: string | null
): string {
  const normalizedSlug = normalizeStoragePart(slug) || "public";
  const normalizedCustomerId = normalizeStoragePart(customerId);
  return [STORAGE_PREFIX, normalizedSlug, normalizedCustomerId].filter(Boolean).join(":");
}

export function buildCustomerReviewPromptStorageValue(
  date = new Date(),
  seenAppointmentIds: string[] = []
): string {
  return JSON.stringify({
    lastSubmittedDateKey: getLocalDateKey(date),
    lastSubmittedAt: date.toISOString(),
    seenAppointmentIds: Array.from(new Set(seenAppointmentIds.filter(Boolean))),
  });
}

export function parseCustomerReviewPromptStorageValue(
  value?: string | null
): CustomerReviewPromptStorageState | null {
  const raw = String(value || "").trim();
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<CustomerReviewPromptStorageState>;
    const lastSubmittedDateKey = String(parsed.lastSubmittedDateKey || "").trim();
    if (!lastSubmittedDateKey) return null;
    const lastSubmittedAt = parsed.lastSubmittedAt
      ? String(parsed.lastSubmittedAt)
      : null;
    const state: CustomerReviewPromptStorageState = {
      lastSubmittedDateKey,
      lastSubmittedAt,
    };
    if (Array.isArray(parsed.seenAppointmentIds)) {
      state.seenAppointmentIds = parsed.seenAppointmentIds
        .map((id) => String(id || "").trim())
        .filter(Boolean);
    }
    return state;
  } catch {
    return null;
  }
}

export function selectLatestPendingCustomerReview(
  reviews: CustomerReviewItem[]
): CustomerReviewItem | null {
  return (
    [...reviews]
      .filter((review) => review.canReview && !review.reviewedAt)
      .sort((left, right) => {
        const leftTime = new Date(left.scheduledAt).getTime();
        const rightTime = new Date(right.scheduledAt).getTime();
        return rightTime - leftTime;
      })[0] || null
  );
}

export function shouldOpenCustomerReviewPrompt({
  now = new Date(),
  lastSubmittedDateKey,
  lastSubmittedAt,
  latestPendingReviewRequestedAt,
  pendingCount,
  seenAppointmentIds,
  pendingAppointmentIds,
}: {
  now?: Date;
  lastSubmittedDateKey?: string | null;
  lastSubmittedAt?: string | null;
  latestPendingReviewRequestedAt?: string | null;
  pendingCount: number;
  seenAppointmentIds?: string[] | null;
  pendingAppointmentIds?: string[] | null;
}): boolean {
  if (pendingCount <= 0) return false;
  if (lastSubmittedDateKey !== getLocalDateKey(now)) return true;
  if (Array.isArray(seenAppointmentIds) && Array.isArray(pendingAppointmentIds)) {
    const seen = new Set(seenAppointmentIds);
    return pendingAppointmentIds.some((id) => !seen.has(id));
  }
  // Valor salvo antes da lista de ids: cai na comparacao por horario.
  if (!lastSubmittedAt || !latestPendingReviewRequestedAt) return false;

  const submittedAtTime = new Date(lastSubmittedAt).getTime();
  const pendingRequestedAtTime = new Date(latestPendingReviewRequestedAt).getTime();
  if (!Number.isFinite(submittedAtTime) || !Number.isFinite(pendingRequestedAtTime)) {
    return false;
  }
  return pendingRequestedAtTime > submittedAtTime;
}
